import React from "react";
import { Clock, ArrowRight } from "lucide-react";
import { TOP_TOURS, SPIRITUAL_TOURS, VISA_FREE, THEMES } from "../mock";
import { Badge } from "./ui/badge";

export function TopToursSection({ id = "top-tours", title, subtitle, ctaLabel, tours, bg = "white" }) {
  const bgClass = bg === "mint" ? "bg-[#E7F3F2]" : "bg-white";
  return (
    <section id={id} className={`${bgClass} py-16 md:py-24`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
            Handcrafted Journeys
          </p>
          <h2
            className="text-3xl md:text-5xl text-slate-900 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h2>
          {subtitle && <p className="text-slate-600 mt-3">{subtitle}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tours.map((t, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={t.image}
                  alt={t.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-[#F5C05B] text-slate-900 hover:bg-[#F5C05B] font-semibold rounded-md px-2.5 py-1">
                    {t.tag}
                  </Badge>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[#1F5B63] transition-colors">
                  {t.title}
                </h3>
                <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
                  <Clock className="h-4 w-4" />
                  <span>{t.days} • {t.nights}</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <div>
                    <p className="text-xs text-slate-500">Starting from</p>
                    <p className="text-xl font-extrabold text-[#1F5B63]">{t.price}</p>
                  </div>
                  <button className="inline-flex items-center gap-1 text-[#1F5B63] font-semibold text-sm hover:gap-2 transition-all">
                    View Details <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {ctaLabel && (
          <div className="text-center mt-12">
            <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#1F5B63] text-white font-semibold hover:bg-[#164a51] transition-colors">
              {ctaLabel} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export function ThemesSection() {
  return (
    <section id="themes" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
            Curated For You
          </p>
          <h2
            className="text-3xl md:text-5xl text-slate-900 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Explore Holidays By Theme
          </h2>
          <p className="text-slate-600 mt-3">
            Find your perfect getaway, tailored to your interests.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {THEMES.map((t) => (
            <a
              key={t.name}
              href="#top"
              className="group relative overflow-hidden rounded-2xl aspect-square shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
            >
              <img
                src={t.image}
                alt={t.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-3 text-white text-center">
                <h3 className="text-base md:text-lg font-bold">{t.name}</h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function VisaFreeSection() {
  return (
    <section className="py-16 md:py-24 bg-[#FBF6EC]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
            No Visa Hassle
          </p>
          <h2
            className="text-3xl md:text-5xl text-slate-900 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Visa-Free Destinations
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {VISA_FREE.map((v) => (
            <a
              key={v.name}
              href="#top"
              className="group relative overflow-hidden rounded-3xl aspect-[3/4] shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
            >
              <img
                src={v.image}
                alt={v.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <h3
                  className="text-2xl font-bold mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {v.name}
                </h3>
                <p className="text-white/85 text-sm">
                  Starting @ <span className="text-[#F5C05B] font-bold">₹{v.price}</span>
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SpiritualToursSection() {
  return (
    <TopToursSection
      id="spiritual"
      title="Spiritual Tours with Soul"
      subtitle="Sacred trails to India’s most divine destinations."
      ctaLabel="Explore All Yatras"
      tours={SPIRITUAL_TOURS}
      bg="mint"
    />
  );
}
